import React, { useState, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { startCase } from 'lodash'
import { Modal, ModalActions } from 'components/Modal/Modal'
import { Form, Select } from 'antd'
import Input from 'components/Input/Input'
import messages from './messages'
import styles from './ActionsModal.scss'

const allowedModes = ['create', 'edit', 'delete', 'view'] as const
const [MODE_CREATE, MODE_EDIT, MODE_DELETE, MODE_VIEW] = allowedModes

type Mode = typeof allowedModes[number]

interface FieldOption {
  id?: string | number
  value?: string | number
  label?: string
  name?: string
}

interface Field {
  key: string
  isRequired?: boolean
  isReadonly?: boolean
  disabled?: boolean
  isDisplayed?: boolean
  formFeedback?: string | React.ReactNode
  label?: string | React.ReactNode
  type?: string
  options?: FieldOption[]
  renderInput?: (props: { disabled: boolean }) => React.ReactNode
  placeholder?: string
  validator?: (value: any) => boolean
}

interface ActionsModalProps {
  type: string
  mode?: Mode
  item: Record<string, any>
  primaryKey: string
  fields?: Record<string, Field>
  onFieldChange: (key: string, value: any) => void
  onClose: () => void
  onSubmit: () => void
  children?: React.ReactNode
  customValidation?: boolean
}

export default function ActionsModal({
  type,
  mode = MODE_CREATE,
  item,
  primaryKey,
  fields = {},
  onFieldChange,
  onClose,
  onSubmit,
  children,
  customValidation,
}: ActionsModalProps) {
  const [invalidValues, setInvalidValues] = useState<Record<string, boolean>>({})

  const onSubmitForm = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit()
  }

  const onChange = (key: string, value: any) => {
    onFieldChange(key, value)

    const field = fields[key]
    const validator = field?.validator || (() => true)
    const isValid = validator(value)

    setInvalidValues({
      ...invalidValues,
      [key]: !isValid,
    })
  }

  const renderHeader = () => (
    <header className={styles.header}>
      <FormattedMessage
        {...messages.header}
        values={{
          mode,
          type,
          itemName: item[primaryKey],
        }}
        tagName='span'
      />
    </header>
  )

  const areRequiredFieldsFilled = useMemo(() => {
    if (mode === MODE_VIEW || mode === MODE_DELETE) return true
    
    return Object.entries(fields)
      .filter(([_, field]) => field.isRequired === true)
      .every(([key]) => {
        const value = item[key]
        return value !== undefined && value !== null && value !== ''
      })
  }, [mode, fields, item])

  const isSubmitDisabled = useMemo(() => {
    if (mode === MODE_DELETE) return false
    
    const hasInvalidValues = Object.values(invalidValues).some(x => x)
    const missingRequiredFields = !areRequiredFieldsFilled
    const customValidationFailed = customValidation !== undefined && customValidation === false
    
    return hasInvalidValues || missingRequiredFields || customValidationFailed
  }, [mode, invalidValues, areRequiredFieldsFilled, customValidation])

  const formActions = [
    {
      key: 'submit',
      type: 'submit' as const,
      color: 'success' as const,
      outline: true,
      isDisplayed: mode !== MODE_VIEW,
      label: <FormattedMessage {...(messages.actions as any).submit} />,
      disabled: isSubmitDisabled,
      onClick: onSubmit,
    },
  ]

  return (
    <Modal isOpen size='lg' onClose={onClose} header={renderHeader()}>
      <div className={styles.container}>
        <Form onFinish={onSubmitForm} className={styles.form} layout="vertical">
          {mode === MODE_DELETE && (
            <div className={styles.deleteHint}>
              <FormattedMessage
                {...messages.deleteHint}
                values={{
                  type,
                  itemName: <span className={styles.name}>{item[primaryKey]}</span>,
                }}
                tagName='span'
              />
            </div>
          )}
          {mode !== MODE_DELETE &&
            Object.values(fields)
              .filter(({ isDisplayed = true }) => isDisplayed)
              .map(
                ({
                  key,
                  isRequired = false,
                  isReadonly = false,
                  disabled = false,
                  formFeedback,
                  label,
                  type: fieldType = 'text',
                  options,
                  renderInput,
                  placeholder,
                }) => {
                  const readonly = mode === MODE_VIEW || isReadonly || disabled

                  const hasValue = !readonly && !!item[key]
                  const isValid = hasValue && !invalidValues[key]
                  const isInvalid = hasValue && invalidValues[key]

                  return (
                    <Form.Item 
                      key={key} 
                      className={styles.group}
                      label={
                        <div className={styles.label}>
                          {isRequired && (
                            <span className={styles.required} title='Required'>
                              *
                            </span>
                          )}
                          {label}
                        </div>
                      }
                      validateStatus={isInvalid ? 'error' : isValid ? 'success' : ''}
                      help={formFeedback && isInvalid ? formFeedback : null}
                    >
                      {renderInput ? (
                        renderInput({ disabled: readonly })
                      ) : (
                        <div>
                          {fieldType === 'select' ? (
                            <Select
                              id={key}
                              disabled={readonly}
                              value={item[key]}
                              onChange={(value) => onChange(key, value)}
                              placeholder={placeholder}
                              className={styles.input}
                              options={options?.map(option => ({
                                value: option.id || option.value,
                                label: startCase(option.label || option.name || '')
                              }))}
                            />
                          ) : (
                            <Input
                              id={key}
                              name={key}
                              type={(fieldType as 'text' | 'number' | 'search' | 'select' | 'checkbox' | 'range' | 'radio' | 'password') || 'text'}
                              disabled={readonly}
                              value={item[key]}
                              onChange={(value) => onChange(key, value)}
                              placeholder={placeholder}
                              className={styles.input}
                            />
                          )}
                        </div>
                      )}
                    </Form.Item>
                  )
                },
              )}
          {children}
        </Form>
      </div>
      <ModalActions actions={formActions} />
    </Modal>
  )
}

